import { useCallback, useEffect, useRef, useState } from 'react';
import Sortable from 'sortablejs';
import { sanitizeRichHtml } from '../utils/sanitizeRichHtml.js';
import './BlogPreviewModal.css';

const PLACEHOLDER_IMG =
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=80';

const IMG_ALIGN_CLASSES = [
  'blog-img-align-default',
  'blog-img-align-center',
  'blog-img-align-wide',
  'blog-img-align-left',
  'blog-img-align-right'
];

const BENTO_3_SLOT_CLASSES = ['blog-bento-3--a', 'blog-bento-3--b', 'blog-bento-3--c'];
const BENTO_6_SLOT_CLASSES = [
  'blog-bento-cell--r1a',
  'blog-bento-cell--r1b',
  'blog-bento-cell--r2a',
  'blog-bento-cell--r2b',
  'blog-bento-cell--r3a',
  'blog-bento-cell--r3b'
];

function stripBentoSlotClasses(cell) {
  if (!cell?.classList) return;
  [...BENTO_3_SLOT_CLASSES, ...BENTO_6_SLOT_CLASSES].forEach((c) => cell.classList.remove(c));
}

/** After Sortable reorder, re-map slot classes so asymmetric 3/6 layouts match DOM order. */
function normalizeBentoGridSlotClasses(grid) {
  if (!grid?.querySelectorAll) return;
  const cells = [...grid.querySelectorAll(':scope > .blog-bento-cell')];
  cells.forEach(stripBentoSlotClasses);
  if (grid.classList.contains('blog-bento-grid--tiles-3')) {
    cells.forEach((cell, i) => {
      if (BENTO_3_SLOT_CLASSES[i]) cell.classList.add(BENTO_3_SLOT_CLASSES[i]);
    });
  } else if (grid.classList.contains('blog-bento-grid--tiles-6')) {
    cells.forEach((cell, i) => {
      if (BENTO_6_SLOT_CLASSES[i]) cell.classList.add(BENTO_6_SLOT_CLASSES[i]);
    });
  }
}

function stripImgAlignClasses(el) {
  if (!el || !el.classList) return;
  IMG_ALIGN_CLASSES.forEach((c) => el.classList.remove(c));
}

function setImgAlign(el, align) {
  if (!el || el.tagName !== 'IMG') return;
  stripImgAlignClasses(el);
  if (align && align !== 'default') el.classList.add(`blog-img-align-${align}`);
}

function formatPostDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return '';
  }
}

/**
 * Full-screen style preview matching the public blog article.
 * Layout mode: drag blocks to reorder; click images for alignment presets (saved as classes on <img>).
 */
export default function BlogPreviewModal({
  open,
  blog,
  isUnsavedPreview = false,
  onClose,
  onEditInAdmin,
  onSaveLayout,
  publicSiteBaseUrl = ''
}) {
  const proseRef = useRef(null);
  const sortableRootRef = useRef(null);
  const gridSortablesRef = useRef([]);
  const [layoutMode, setLayoutMode] = useState(false);
  const [imgMenu, setImgMenu] = useState(null);
  const [saving, setSaving] = useState(false);

  const hydrateProse = useCallback(() => {
    const root = proseRef.current;
    if (!root || !blog) return;
    root.innerHTML = sanitizeRichHtml(String(blog.content || ''));
    /* <img> defaults to draggable=true in browsers → native URL-drag steals events from Sortable. */
    root.querySelectorAll('img').forEach((img) => {
      img.draggable = false;
    });
  }, [blog]);

  useEffect(() => {
    if (!open || !blog) return;
    hydrateProse();
    setLayoutMode(false);
    setImgMenu(null);
  }, [open, blog?.id, blog?.content, hydrateProse]);

  useEffect(() => {
    const destroyLayoutSortables = () => {
      gridSortablesRef.current.forEach((s) => {
        try {
          s?.destroy();
        } catch {
          /* noop */
        }
      });
      gridSortablesRef.current = [];
      if (sortableRootRef.current) {
        try {
          sortableRootRef.current.destroy();
        } catch {
          /* noop */
        }
        sortableRootRef.current = null;
      }
    };

    if (!open || !layoutMode || !proseRef.current) {
      destroyLayoutSortables();
      return undefined;
    }

    const root = proseRef.current;
    if (!root.children?.length) return destroyLayoutSortables;

    root.querySelectorAll('.blog-bento-grid').forEach((grid) => {
      if (!grid.querySelector('.blog-bento-cell')) return;
      const s = Sortable.create(grid, {
        animation: 160,
        ghostClass: 'blog-preview-bento-ghost',
        chosenClass: 'blog-preview-bento-chosen',
        dragClass: 'blog-preview-bento-drag',
        draggable: '.blog-bento-cell',
        filter: '.blog-preview-img-popover, a, button',
        preventOnFilter: false,
        swapThreshold: 0.55,
        invertSwap: true,
        emptyInsertThreshold: 32,
        fallbackOnBody: true,
        forceFallback: true,
        bubbleScroll: true,
        scrollSensitivity: 48,
        scrollSpeed: 12,
        onEnd: () => normalizeBentoGridSlotClasses(grid)
      });
      gridSortablesRef.current.push(s);
    });

    sortableRootRef.current = Sortable.create(root, {
      animation: 160,
      ghostClass: 'blog-preview-sortable-ghost',
      chosenClass: 'blog-preview-sortable-chosen',
      dragClass: 'blog-preview-sortable-drag',
      draggable: '> *',
      filter: '.blog-preview-img-popover, a, button',
      preventOnFilter: false,
      /* Easier to drop between paragraphs (middle) inside the scrollable preview; avoids top/end-only. */
      direction: 'vertical',
      swapThreshold: 0.55,
      invertSwap: true,
      emptyInsertThreshold: 72,
      fallbackOnBody: true,
      forceFallback: true,
      fallbackTolerance: 4,
      bubbleScroll: true,
      scrollSensitivity: 48,
      scrollSpeed: 12
    });

    return destroyLayoutSortables;
  }, [open, layoutMode, blog?.content, blog?.id]);

  useEffect(() => {
    if (!open || !layoutMode || !proseRef.current) return;
    const root = proseRef.current;
    const onClick = (e) => {
      const t = e.target;
      if (t && t.tagName === 'IMG') {
        e.preventDefault();
        e.stopPropagation();
        const r = t.getBoundingClientRect();
        setImgMenu({
          el: t,
          left: Math.min(r.left, window.innerWidth - 300),
          top: r.bottom + 8
        });
      }
    };
    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [open, layoutMode, blog?.content]);

  useEffect(() => {
    if (!imgMenu) return undefined;
    let registered = false;
    const close = (e) => {
      if (e?.target?.closest?.('.blog-preview-img-popover')) return;
      setImgMenu(null);
    };
    const id = setTimeout(() => {
      registered = true;
      window.addEventListener('mousedown', close);
    }, 0);
    return () => {
      clearTimeout(id);
      if (registered) window.removeEventListener('mousedown', close);
    };
  }, [imgMenu]);

  if (!open || !blog) return null;

  const heroImage = (blog.imageUrl && String(blog.imageUrl).trim()) || PLACEHOLDER_IMG;
  const categoryLabel = (blog.category && String(blog.category).trim()) || 'Blog';
  const published = blog.publishedAt || blog.createdAt;
  const dateStr = formatPostDate(published);
  const liveBase = String(publicSiteBaseUrl || '').replace(/\/$/, '');
  const liveUrl =
    blog.slug && liveBase ? `${liveBase}/blogs/${encodeURIComponent(String(blog.slug).trim())}` : '';

  const handleSaveLayout = async () => {
    if (!proseRef.current || !blog?.id || typeof onSaveLayout !== 'function') return;
    setSaving(true);
    try {
      const raw = proseRef.current.innerHTML;
      await onSaveLayout(raw, blog.id);
      hydrateProse();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="blog-preview-overlay" role="presentation" onClick={onClose}>
      <div
        className="blog-preview-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="blog-preview-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="blog-preview-toolbar">
          <h2 id="blog-preview-title">Blog preview</h2>
          <div className="blog-preview-toolbar-actions">
            <label>
              <input
                type="checkbox"
                checked={layoutMode}
                onChange={(e) => {
                  setLayoutMode(e.target.checked);
                  setImgMenu(null);
                }}
              />
              Layout mode
            </label>
            {blog.id && !isUnsavedPreview ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving}
                title="Saves block order and image alignment classes to this post"
                onClick={handleSaveLayout}
              >
                {saving ? 'Saving…' : 'Save layout'}
              </button>
            ) : null}
            {liveUrl ? (
              <a className="btn btn-secondary" href={liveUrl} target="_blank" rel="noopener noreferrer">
                Open live
              </a>
            ) : null}
            <button type="button" className="btn btn-secondary" onClick={() => onEditInAdmin?.(blog)}>
              Edit in admin
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <p className="blog-preview-hint">
          Read-only snapshot of the public article (you cannot type here). Change titles and body copy in the admin
          rich editor, then open preview again. <strong>Layout mode</strong>: drag a <strong>whole block</strong> (grab
          the paragraph’s text or margin — not only the image center) to reorder; drop on the <strong>gap between
          blocks</strong> to place content in the middle. Photo grids: drag tiles to swap order. Two images
          <strong>side by side</strong> in the article need a <strong>photo grid</strong> or both images in one
          paragraph from the editor — layout mode only reorders blocks, not in-paragraph positions. Click an image
          for alignment.
          {isUnsavedPreview || !blog.id
            ? ' Save the post first to persist layout changes from this screen.'
            : null}
        </p>

        <div className="blog-preview-scroll">
          <article className="blog-preview-article">
            <header
              className="blog-preview-hero"
              style={{
                backgroundImage: `linear-gradient(to top, rgba(15,23,42,0.92) 0%, rgba(15,23,42,0.5) 45%, rgba(15,23,42,0.2) 100%), url(${heroImage})`
              }}
            >
              <div className="blog-preview-hero-inner">
                <span className="blog-preview-hero-category">{categoryLabel.toUpperCase()}</span>
                <h1 className="blog-preview-hero-title">{blog.title || 'Untitled'}</h1>
              </div>
            </header>

            <div className="blog-preview-meta">
              <div className="blog-preview-meta-inner">
                {blog.author ? <span>By {blog.author}</span> : null}
                {blog.author && dateStr ? <span> · </span> : null}
                {dateStr ? <span>{dateStr}</span> : null}
                {blog.category ? (
                  <>
                    <span> · </span>
                    <span>{String(blog.category).trim()}</span>
                  </>
                ) : null}
              </div>
            </div>

            <div className="blog-preview-body-wrap">
              {blog.shortDescription ? (
                <p className="blog-preview-lead">{String(blog.shortDescription).trim()}</p>
              ) : null}
              <div
                ref={proseRef}
                className={`blog-preview-prose rich-content-html${layoutMode ? ' layout-mode' : ''}`}
              />
            </div>
          </article>
        </div>
      </div>

      {imgMenu && layoutMode ? (
        <div
          className="blog-preview-img-popover"
          style={{ left: imgMenu.left, top: imgMenu.top }}
          role="menu"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="blog-preview-popover-title">Image alignment</div>
          {['default', 'center', 'wide', 'left', 'right'].map((align) => (
            <button
              key={align}
              type="button"
              onClick={() => {
                setImgAlign(imgMenu.el, align);
                setImgMenu(null);
              }}
            >
              {align === 'default' ? 'Default' : align.charAt(0).toUpperCase() + align.slice(1)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
